// eslint-disable-next-line wix-editor/no-internal-import
import {STYLE} from '@wix/component-protocol/schema'
import type {Builder} from '@wix/document-services-types'

interface ShortHandExpansionDef {
    propertyName: string
    cssPropertyType: Builder.StyleType
}

const getBorderExpansion = (propertyName: string): ShortHandExpansionDef[] => [
    {propertyName: `${propertyName}Width`, cssPropertyType: STYLE.CSS_PROPERTY_TYPE.borderWidth},
    {propertyName: `${propertyName}Style`, cssPropertyType: STYLE.CSS_PROPERTY_TYPE.borderStyle},
    {propertyName: `${propertyName}Color`, cssPropertyType: STYLE.CSS_PROPERTY_TYPE.borderColor}
]

export const CSS_SHORTHAND_MAPPINGS: Record<string, ShortHandExpansionDef[]> = {
    // Border properties - all follow the same pattern
    [STYLE.CSS_PROPERTY_TYPE.border]: getBorderExpansion('border'),
    [STYLE.CSS_PROPERTY_TYPE.borderRight]: getBorderExpansion('borderRight'),
    [STYLE.CSS_PROPERTY_TYPE.borderLeft]: getBorderExpansion('borderLeft'),
    [STYLE.CSS_PROPERTY_TYPE.borderTop]: getBorderExpansion('borderTop'),
    [STYLE.CSS_PROPERTY_TYPE.borderBottom]: getBorderExpansion('borderBottom'),
    [STYLE.CSS_PROPERTY_TYPE.borderInlineStart]: getBorderExpansion('borderInlineStart'),
    [STYLE.CSS_PROPERTY_TYPE.borderInlineEnd]: getBorderExpansion('borderInlineEnd'),

    // Border width expansion
    [STYLE.CSS_PROPERTY_TYPE.borderWidth]: [
        {propertyName: 'borderTopWidth', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.borderTopWidth},
        {propertyName: 'borderBottomWidth', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.borderTopWidth},
        {propertyName: 'borderLeftWidth', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.borderTopWidth},
        {propertyName: 'borderRightWidth', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.borderTopWidth}
    ],

    // Border style expansion
    [STYLE.CSS_PROPERTY_TYPE.borderStyle]: [
        {propertyName: 'borderTopStyle', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.borderTopStyle},
        {propertyName: 'borderBottomStyle', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.borderTopStyle},
        {propertyName: 'borderLeftStyle', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.borderTopStyle},
        {propertyName: 'borderRightStyle', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.borderTopStyle}
    ],

    // Border color expansion
    [STYLE.CSS_PROPERTY_TYPE.borderColor]: [
        {propertyName: 'borderTopColor', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.color},
        {propertyName: 'borderBottomColor', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.color},
        {propertyName: 'borderLeftColor', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.color},
        {propertyName: 'borderRightColor', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.color}
    ],

    // Border radius expansion
    [STYLE.CSS_PROPERTY_TYPE.borderRadius]: [
        {propertyName: 'borderTopLeftRadius', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.borderTopLeftRadius},
        {propertyName: 'borderTopRightRadius', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.borderTopLeftRadius},
        {propertyName: 'borderBottomLeftRadius', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.borderTopLeftRadius},
        {propertyName: 'borderBottomRightRadius', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.borderTopLeftRadius}
    ],

    // Padding expansion
    [STYLE.CSS_PROPERTY_TYPE.padding]: [
        {propertyName: 'paddingTop', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.paddingTop},
        {propertyName: 'paddingBottom', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.paddingTop},
        {propertyName: 'paddingLeft', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.paddingTop},
        {propertyName: 'paddingRight', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.paddingTop}
    ],

    // Margin expansion
    [STYLE.CSS_PROPERTY_TYPE.margin]: [
        {propertyName: 'marginTop', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.marginTop},
        {propertyName: 'marginBottom', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.marginTop},
        {propertyName: 'marginLeft', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.marginTop},
        {propertyName: 'marginRight', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.marginTop}
    ],

    // Background expansion
    [STYLE.CSS_PROPERTY_TYPE.background]: [
        {propertyName: 'backgroundImage', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.backgroundImage},
        {propertyName: 'backgroundPosition', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.backgroundPosition},
        {propertyName: 'backgroundSize', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.backgroundSize},
        {propertyName: 'backgroundRepeat', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.backgroundRepeat},
        {propertyName: 'backgroundAttachment', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.backgroundAttachment},
        {propertyName: 'backgroundOrigin', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.backgroundOrigin},
        {propertyName: 'backgroundClip', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.backgroundClip},
        {propertyName: 'backgroundColor', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.backgroundColor}
    ],

    // Overflow expansion
    [STYLE.CSS_PROPERTY_TYPE.overflow]: [
        {propertyName: 'overflowX', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.overflow},
        {propertyName: 'overflowY', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.overflow}
    ],

    // Font expansion
    [STYLE.CSS_PROPERTY_TYPE.font]: [
        {propertyName: 'fontSize', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.fontSize},
        {propertyName: 'fontStyle', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.fontStyle},
        {propertyName: 'fontWeight', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.fontWeight},
        {propertyName: 'fontFamily', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.fontFamily},
        {propertyName: 'fontVariant', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.fontVariant},
        {propertyName: 'fontStretch', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.fontStretch},
        {propertyName: 'lineHeight', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.lineHeight}
    ],

    // Text decoration expansion
    [STYLE.CSS_PROPERTY_TYPE.textDecoration]: [
        {propertyName: 'textDecorationLine', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.textDecorationLine},
        {propertyName: 'textDecorationStyle', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.textDecorationStyle},
        {propertyName: 'textDecorationThickness', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.textDecorationThickness},
        {propertyName: 'textDecorationColor', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.color}
    ],

    // Gap expansion
    [STYLE.CSS_PROPERTY_TYPE.gap]: [
        {propertyName: 'rowGap', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.rowGap},
        {propertyName: 'columnGap', cssPropertyType: STYLE.CSS_PROPERTY_TYPE.columnGap}
    ]
}
